import { useState, useRef } from "react";
import { H3, Label } from "../Text";
import { toast } from "react-toastify";
import Circle from "../Animation/Circle";
import { Button } from "../Buttons";
import { Request } from "../../hooks/Request";
import { requestAuth } from "../services/RequestService";
import { useDialog } from "../../contexts/DialogContext";
import { DialogConfirmation } from "../Modal";
import { ImportTrackingModal } from "../../containers/ManegeProcedure/ImportTrackingModal";
import { useAuth } from "../../contexts/AuthContext";
import axios from "axios"; // Necesario para la subida de archivos manual

// --- CONFIGURACIÓN VISUAL ---
const CATEGORIES = {
  LOGISTICA: {
    label: "Logística",
    icon: "fas fa-ship",
    color: "bg-blue-500",
    border: "border-blue-500",
    light: "bg-blue-50 text-blue-700",
  },
  ADUANA: {
    label: "Aduanas",
    icon: "fas fa-balance-scale",
    color: "bg-purple-600",
    border: "border-purple-600",
    light: "bg-purple-50 text-purple-700",
  },
  PAGO: {
    label: "Finanzas",
    icon: "fas fa-file-invoice-dollar",
    color: "bg-emerald-500",
    border: "border-emerald-500",
    light: "bg-emerald-50 text-emerald-700",
  },
  DOCS: {
    label: "Documentos",
    icon: "fas fa-folder-open",
    color: "bg-orange-500",
    border: "border-orange-500",
    light: "bg-orange-50 text-orange-700",
  },
  ALERTA: {
    label: "Incidencia",
    icon: "fas fa-exclamation-triangle",
    color: "bg-red-500",
    border: "border-red-500",
    light: "bg-red-50 text-red-700",
  },
  OTRO: {
    label: "General",
    icon: "fas fa-star",
    color: "bg-gray-500",
    border: "border-gray-500",
    light: "bg-gray-100 text-gray-600",
  },
};

// --- UTILIDADES DE FECHA ---
const formatDate = (dateString) => {
  if (!dateString) return { day: "--", month: "---", year: "----", full: "" };
  const d = new Date(dateString);
  const months = [
    "ENE",
    "FEB",
    "MAR",
    "ABR",
    "MAY",
    "JUN",
    "JUL",
    "AGO",
    "SEP",
    "OCT",
    "NOV",
    "DIC",
  ];
  return {
    day: d.getDate(),
    month: months[d.getMonth()],
    year: d.getFullYear(),
    full:
      d.toLocaleDateString() +
      " " +
      d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
  };
};

const safeJsonParse = (value, fallback) => {
  try {
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
};

const getEventComments = (meta) =>
  Array.isArray(meta?.comments) ? meta.comments : [];

const getCurrentUserName = (authUser) => {
  const user = authUser?.user;
  return (
    [user?.name, user?.fatherLastName, user?.motherLastName]
      .filter(Boolean)
      .join(" ") ||
    user?.userName ||
    user?.email ||
    "Usuario"
  );
};

const InputSubDataFollowUpAdd = ({ field }) => {
  const { openDialog, dialogClose } = useDialog();
  const { user } = useAuth();
  const [carouselIndexMap, setCarouselIndexMap] = useState({});
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [panMap, setPanMap] = useState({});
  const [dragMap, setDragMap] = useState({});
  const [lbPan, setLbPan] = useState({ x: 0, y: 0 });
  const [lbScale, setLbScale] = useState(1);
  const [lbDrag, setLbDrag] = useState(null);
  const [commentDrafts, setCommentDrafts] = useState({});
  const [savingCommentId, setSavingCommentId] = useState(null);

  function isImageFile(file) {
    const name = file.fileName || file.url || "";
    return /\.(png|jpe?g|gif|webp|bmp)$/i.test(name);
  }

  function getImages(files) {
    return files.filter(isImageFile);
  }

  function getDocs(files) {
    return files.filter((f) => !isImageFile(f));
  }

  const {
    data: followUpData,
    loading,
    call: reloadData,
  } = Request({
    urlApi: `/dataSet/procedure/${field.id}/field/${field.fieldId}/subdata`,
  });

  // Ordenar: Eventos más recientes arriba
  const sortedData =
    followUpData ? [...followUpData].sort((a, b) => {
      const dateA = safeJsonParse(a.description2, {})?.eventDate || a.createdAt;
      const dateB = safeJsonParse(b.description2, {})?.eventDate || b.createdAt;
      return new Date(dateB) - new Date(dateA);
    }) : [];

  async function updateEventMeta(item, nextMeta) {
    await requestAuth("put", `/dataSet/procedure/subData/${item.id}`, {
      ...item,
      Label: item.label || item.Label || "",
      Description2: JSON.stringify(nextMeta),
      Description4: item.description4 || item.Description4 || "[]",
      dataSetId: field.dataSetId,
    });
  }

  function getCommentKey(item, comment) {
    return `${item.id}-${comment.id || comment.__index}`;
  }

  function isSameComment(currentComment, currentIndex, targetComment) {
    return targetComment.id
      ? currentComment.id === targetComment.id
      : currentIndex === targetComment.__index;
  }

  async function handleAddComment(item) {
    const text = (commentDrafts[item.id] || "").trim();
    if (!text) {
      toast.warning("Ingrese un comentario");
      return;
    }

    const meta = safeJsonParse(item.description2, {});
    const comments = getEventComments(meta);
    const nextComment = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      text,
      createdAt: new Date().toISOString(),
      createdBy: getCurrentUserName(user),
    };

    try {
      setSavingCommentId(item.id);
      await updateEventMeta(item, {
        ...meta,
        comments: [...comments, nextComment],
      });
      setCommentDrafts((current) => ({ ...current, [item.id]: "" }));
      toast.success("Comentario agregado");
      reloadData();
    } catch (error) {
      console.error(error);
      toast.error("Error al guardar el comentario");
    } finally {
      setSavingCommentId(null);
    }
  }

  function handleDeleteComment(item, comment) {
    const meta = safeJsonParse(item.description2, {});
    const comments = getEventComments(meta);
    const updatedComments = comments.filter(
      (currentComment, currentIndex) =>
        !isSameComment(currentComment, currentIndex, comment),
    );

    openDialog(
      <DialogConfirmation
        children={<H3>¿Eliminar este comentario?</H3>}
        onClose={dialogClose}
        method="put"
        url={`/dataSet/procedure/subData/${item.id}`}
        texBtn="Eliminar"
        data={{
          ...item,
          Label: item.label || item.Label || "",
          Description2: JSON.stringify({
            ...meta,
            comments: updatedComments,
          }),
          Description4: item.description4 || item.Description4 || "[]",
          dataSetId: field.dataSetId,
        }}
        handleSuccess={reloadData}
      />,
    );
  }

  const handleImportTracking = () => {
    openDialog(<ImportTrackingModal procedure={{ id: field.id }} />);
  };

  const handleForm = (item = null) => {
    openDialog(
      <FormAddFollowUp
        existingItem={item}
        field={field}
        reload={reloadData}
        onClose={dialogClose}
      />,
    );
  };

  const handleDelete = (data) => {
    openDialog(
      <DialogConfirmation
        children={<H3>¿Eliminar este hito?</H3>}
        onClose={dialogClose}
        method="delete"
        url={`/dataSet/procedure/subData/${data.id}`}
        texBtn="Confirmar Eliminación"
        handleSuccess={reloadData}
      />,
    );
  };

  // Usamos un modal específico para gestionar documentos con metadata
  const handleFiles = (item) => {
    openDialog(
      <FollowUpFileManager
        item={item}
        reload={reloadData}
        onClose={dialogClose}
        field={field}
      />,
    );
  };

  return (
    <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-6 rounded-2xl border border-gray-200 shadow-sm">
      {/* Header Alucinante */}
      <div className="flex justify-between items-end mb-8 border-b border-gray-200 pb-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 tracking-tight">
            <span aria-hidden="true" className="fas fa-route text-blue-600 mr-2"></span>
            {field.label}
          </h2>
          <p className="text-sm text-gray-500 mt-1 pl-8">
            Línea de tiempo oficial de eventos y documentación.
          </p>
        </div>
        <Button
          onClick={() => handleForm(null)}
          className="shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all bg-blue-600 text-white px-6 py-2 rounded-full"
        >
          <span aria-hidden="true" className="fas fa-plus mr-2"></span> Registrar Evento
        </Button>
        <Button
          onClick={handleImportTracking}
          className="shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all bg-teal-500 text-white px-6 py-2 rounded-full"
        >
          <span aria-hidden="true" className="fas fa-map-location-dot mr-2"></span> Seguimiento
        </Button>
      </div>

      {!loading ? (
        <div className="flex justify-center py-10">
          <Circle />
        </div>
      ) : (
        <div className="relative container mx-auto px-4">
          {/* Línea Central */}
          <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-gray-300 md:block hidden"></div>

          {sortedData.length === 0 && (
            <div className="text-center py-12 text-gray-400 bg-white rounded-xl border border-dashed border-gray-300">
              <span aria-hidden="true" className="fas fa-wind text-4xl mb-3 opacity-50"></span>
              <p>No hay eventos registrados aún.</p>
            </div>
          )}

          <div className="space-y-8">
            {sortedData.map((item) => {
              const meta = safeJsonParse(item.description2, {});
              const files = safeJsonParse(item.description4, []);
              const cat = CATEGORIES[meta.category] || CATEGORIES.OTRO;
              const comments = getEventComments(meta)
                .map((comment, index) => ({ ...comment, __index: index }))
                .sort(
                (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
              );

              // Fechas
              const eventDateObj = meta.eventDate
                ? new Date(meta.eventDate)
                : new Date(item.createdAt);
              // Corrección de zona horaria simple para visualización
              const userTimezoneOffset =
                eventDateObj.getTimezoneOffset() * 60000;
              const eventDate = new Date(
                eventDateObj.getTime() + userTimezoneOffset,
              );

              const fEvent = formatDate(eventDate);
              const fCreated = formatDate(item.createdAt);

              return (
                <div
                  key={item.id}
                  className="relative flex flex-col md:flex-row gap-6 group"
                >
                  {/* Columna Fecha (Izquierda) */}
                  <div className="md:w-6 flex-shrink-0 flex flex-col items-center md:items-end text-gray-600 pt-2">
                    <span className="text-3xl font-black text-gray-700 leading-none">
                      {fEvent.day}
                    </span>
                    <span className="text-sm font-bold uppercase tracking-wider text-blue-500">
                      {fEvent.month}
                    </span>
                    <span className="text-xs text-gray-400">{fEvent.year}</span>
                  </div>

                  {/* Icono Central */}
                  <div className="absolute left-24 -top-4 -translate-x-1/2 w-10 h-10 rounded-full border-4 border-white shadow-md z-10 hidden md:flex items-center justify-center bg-white">
                    <div
                      className={`w-full h-full rounded-full flex items-center justify-center text-white text-xs ${cat.color}`}
                    >
                      <span aria-hidden="true" className={cat.icon}></span>
                    </div>
                  </div>

                  {/* Tarjeta Contenido (Derecha) */}
                  <div className="flex-grow bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100 overflow-hidden relative">
                    {/* Banda de color categoría */}
                    <div className={`h-1.5 w-full ${cat.color}`}></div>

                    <div className="p-5">
                      {/* Header Tarjeta */}
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <span
                            className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide mb-1 ${cat.light}`}
                          >
                            {cat.label}
                          </span>
                          <h3 className="text-lg font-bold text-gray-800 leading-tight">
                            {item.label}
                          </h3>
                          {meta.location && (
                            <div className="text-xs text-gray-500 mt-1 flex items-center">
                              <span aria-hidden="true" className="fas fa-map-marker-alt mr-1.5 text-red-400"></span>
                              {meta.location}
                            </div>
                          )}
                        </div>

                        {/* Acciones */}
                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                          <button
                            onClick={() => handleForm(item)}
                            className="w-8 h-8 rounded-full bg-gray-50 hover:bg-blue-50 text-gray-400 hover:text-blue-600 transition"
                          >
                            <span aria-hidden="true" className="fas fa-pencil-alt text-xs"></span>
                          </button>
                          <button
                            onClick={() => handleDelete(item)}
                            className="w-8 h-8 rounded-full bg-gray-50 hover:bg-red-50 text-gray-400 hover:text-red-600 transition"
                          >
                            <span aria-hidden="true" className="fas fa-trash text-xs"></span>
                          </button>
                        </div>
                      </div>

                      {/* Comentario */}
                      {meta.comment && (
                        <div className="text-gray-600 text-sm mb-4 bg-gray-50/50 p-3 rounded-lg border border-gray-100">
                          {meta.comment}
                        </div>
                      )}

                      {/* Documentos Adjuntos — Carrusel de imágenes + enlaces PDF */}
                      {files.length > 0 && (
                        <div className="mb-4">
                          <p className="text-[10px] uppercase font-bold text-gray-400 mb-2">
                            Adjuntos ({files.length})
                          </p>
                          {(() => {
                            const images = getImages(files);
                            const docs = getDocs(files);
                            const carIdx = carouselIndexMap[item.id] || 0;
                            const currentImage = images[carIdx];

                            const panKey = `pan_${item.id}`;
                            const dragKey = `drag_${item.id}`;
                            const pan = panMap[panKey] || { x: 0, y: 0, scale: 1 };

                            function resetPan() {
                              setPanMap((p) => ({ ...p, [panKey]: { x: 0, y: 0, scale: 1 } }));
                            }

                            const prevImg = () => {
                              const next = carIdx === 0 ? images.length - 1 : carIdx - 1;
                              setCarouselIndexMap((p) => ({ ...p, [item.id]: next }));
                              resetPan();
                            };
                            const nextImg = () => {
                              const next = carIdx === images.length - 1 ? 0 : carIdx + 1;
                              setCarouselIndexMap((p) => ({ ...p, [item.id]: next }));
                              resetPan();
                            };

                            const handlePanDown = (e) => {
                              setDragMap((p) => ({
                                ...p,
                                [dragKey]: { startX: e.clientX, startY: e.clientY, panX: pan.x, panY: pan.y },
                              }));
                            };

                            const handlePanMove = (e) => {
                              const d = dragMap[dragKey];
                              if (!d) return;
                              const dx = e.clientX - d.startX;
                              const dy = e.clientY - d.startY;
                              setPanMap((p) => ({ ...p, [panKey]: { ...p[panKey] || { scale: 1 }, x: d.panX + dx, y: d.panY + dy } }));
                            };

                            const handlePanUp = (e) => {
                              const d = dragMap[dragKey];
                              if (!d) return;
                              const dx = e.clientX - d.startX;
                              const dy = e.clientY - d.startY;
                              setDragMap((p) => {
                                const next = { ...p };
                                delete next[dragKey];
                                return next;
                              });
                              if (Math.abs(dx) < 3 && Math.abs(dy) < 3) {
                                setLightboxImages(images);
                                setLightboxIndex(carIdx);
                                setLbScale(1);
                                setLbPan({ x: 0, y: 0 });
                              }
                            };

                            const handlePanLeave = () => {
                              setDragMap((p) => {
                                const next = { ...p };
                                delete next[dragKey];
                                return next;
                              });
                            };

                            const handleWheel = (e) => {
                              const delta = e.deltaY > 0 ? -0.15 : 0.15;
                              setPanMap((p) => {
                                const cur = p[panKey] || { x: 0, y: 0, scale: 1 };
                                const newScale = Math.min(5, Math.max(0.5, cur.scale + delta));
                                return { ...p, [panKey]: { ...cur, scale: newScale } };
                              });
                            };

                            return (
                              <>
                                {images.length > 0 && currentImage && (
                                  <div className="relative group mb-2">
                                    <div
                                      className="w-full h-64 overflow-hidden overscroll-contain rounded-lg border border-gray-200 bg-gray-50 cursor-grab active:cursor-grabbing select-none"
                                      onMouseDown={handlePanDown}
                                      onMouseMove={handlePanMove}
                                      onMouseUp={handlePanUp}
                                      onMouseLeave={handlePanLeave}
                                      onWheel={handleWheel}
                                      tabIndex={0}
                                    >
                                      <img
                                        src={currentImage.url}
                                        alt={currentImage.name || "Imagen"}
                                        className="w-full h-full object-contain pointer-events-none"
                                        draggable={false}
                                        style={{
                                          transform: `translate(${pan.x}px, ${pan.y}px) scale(${pan.scale || 1})`,
                                          transformOrigin: 'center center',
                                        }}
                                      />
                                    </div>
                                    <div className="absolute bottom-1 left-1 right-1 flex items-center justify-between pointer-events-none">
                                      <div className="flex items-center gap-1 pointer-events-auto">
                                        <button
                                          onClick={() => {
                                            setPanMap((p) => {
                                              const cur = p[panKey] || { x: 0, y: 0, scale: 1 };
                                              return { ...p, [panKey]: { ...cur, scale: Math.max(0.5, cur.scale - 0.3) } };
                                            });
                                          }}
                                          className="w-6 h-6 rounded bg-black/40 text-white text-xs flex items-center justify-center hover:bg-black/60 transition"
                                          title="Alejar"
                                        >
                                          <span aria-hidden="true" className="fas fa-search-minus"></span>
                                        </button>
                                        <span className="text-[10px] bg-black/50 text-white px-1.5 py-0.5 rounded min-w-[36px] text-center">
                                          {Math.round((pan.scale || 1) * 100)}%
                                        </span>
                                        <button
                                          onClick={() => {
                                            setPanMap((p) => {
                                              const cur = p[panKey] || { x: 0, y: 0, scale: 1 };
                                              return { ...p, [panKey]: { ...cur, scale: Math.min(5, cur.scale + 0.3) } };
                                            });
                                          }}
                                          className="w-6 h-6 rounded bg-black/40 text-white text-xs flex items-center justify-center hover:bg-black/60 transition"
                                          title="Acercar"
                                        >
                                          <span aria-hidden="true" className="fas fa-search-plus"></span>
                                        </button>
                                        {(pan.scale || 1) !== 1 && (
                                          <button
                                            onClick={resetPan}
                                            className="w-6 h-6 rounded bg-black/40 text-white text-xs flex items-center justify-center hover:bg-black/60 transition"
                                            title="Restablecer zoom"
                                          >
                                            <span aria-hidden="true" className="fas fa-undo"></span>
                                          </button>
                                        )}
                                      </div>
                                      {images.length > 1 && (
                                        <div className="flex items-center gap-1 pointer-events-auto">
                                          <button
                                            onClick={prevImg}
                                            className="w-6 h-6 rounded bg-black/40 text-white text-xs flex items-center justify-center hover:bg-black/60 transition"
                                          >
                                            <span aria-hidden="true" className="fas fa-chevron-left"></span>
                                          </button>
                                          <span className="text-[10px] bg-black/50 text-white px-1.5 py-0.5 rounded min-w-[28px] text-center">
                                            {carIdx + 1}/{images.length}
                                          </span>
                                          <button
                                            onClick={nextImg}
                                            className="w-6 h-6 rounded bg-black/40 text-white text-xs flex items-center justify-center hover:bg-black/60 transition"
                                          >
                                            <span aria-hidden="true" className="fas fa-chevron-right"></span>
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                                {docs.map((file, idx) => {
                                  const uploadDate = formatDate(file.uploadDate);
                                  return (
                                    <a
                                      key={idx}
                                      href={file.url}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="flex items-center gap-3 p-2 rounded-lg border border-gray-100 hover:border-blue-200 hover:bg-blue-50/30 transition group/file mb-1"
                                    >
                                      <div className="w-8 h-8 rounded bg-gray-100 flex items-center justify-center text-gray-500 group-hover/file:bg-white group-hover/file:text-blue-500 transition">
                                        <span aria-hidden="true" className="fas fa-file-pdf text-lg"></span>
                                      </div>
                                      <div className="overflow-hidden">
                                        <p className="text-xs font-bold text-gray-700 truncate">
                                          {file.name || "Documento"}
                                        </p>
                                        <p className="text-[10px] text-gray-500 truncate">
                                          {file.description || file.fileName}
                                        </p>
                                        <p className="text-[9px] text-gray-400 mt-0.5">
                                          Subido: {uploadDate.full}
                                        </p>
                                      </div>
                                    </a>
                                  );
                                })}
                              </>
                            );
                          })()}
                        </div>
                      )}

                      <div className="mt-4 border-t border-gray-100 pt-4">
                        <div className="flex items-center justify-between mb-3">
                          <p className="text-[10px] uppercase font-bold text-gray-400">
                            Comentarios / Subeventos ({comments.length})
                          </p>
                        </div>

                        {comments.length > 0 && (
                          <div className="space-y-2 mb-3">
                            {comments.map((comment) => {
                              const commentDate = formatDate(comment.createdAt);
                              const editedDate = formatDate(comment.editedAt);
                              const commentKey = getCommentKey(item, comment);
                              return (
                                <div
                                  key={commentKey}
                                  className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2"
                                >
                                  <div className="flex items-center justify-between gap-2 mb-1">
                                    <div className="min-w-0">
                                      <span className="text-[11px] font-bold text-slate-500">
                                        {comment.createdBy || "Usuario"}
                                      </span>
                                      <span className="text-[10px] text-slate-400 ml-2">
                                        {commentDate.full}
                                      </span>
                                      {comment.editedAt && (
                                        <span className="text-[10px] text-slate-400 ml-2">
                                          Editado {editedDate.full}
                                        </span>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <button
                                        type="button"
                                        onClick={() => handleDeleteComment(item, comment)}
                                        className="text-slate-400 hover:text-red-600 px-1"
                                        title="Eliminar comentario"
                                      >
                                        <span aria-hidden="true" className="fas fa-trash text-[10px]"></span>
                                      </button>
                                    </div>
                                  </div>
                                  <p className="text-xs text-slate-700 whitespace-pre-line">
                                    {comment.text}
                                  </p>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        <div className="flex flex-col sm:flex-row gap-2">
                          <textarea
                            rows="2"
                            value={commentDrafts[item.id] || ""}
                            onChange={(event) =>
                              setCommentDrafts((current) => ({
                                ...current,
                                [item.id]: event.target.value,
                              }))
                            }
                            className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-xs outline-none focus:ring-2 ring-blue-100 resize-none"
                            placeholder="Agregar comentario o subevento..."
                          />
                          <Button
                            onClick={() => handleAddComment(item)}
                            disabled={savingCommentId === item.id}
                            className="self-end sm:self-stretch px-3 py-2 text-xs rounded-lg"
                          >
                            {savingCommentId === item.id ? (
                              <span aria-hidden="true" className="fas fa-spinner fa-spin"></span>
                            ) : (
                              <>
                                <span aria-hidden="true" className="fas fa-comment-medical mr-1"></span>
                                Agregar
                              </>
                            )}
                          </Button>
                        </div>
                      </div>

                      {/* Footer Tarjeta */}
                      <div className="flex justify-between items-center pt-3 border-t border-gray-50 mt-2">
                        <div className="text-[10px] text-gray-400">
                          <span aria-hidden="true" className="fas fa-history mr-1"></span>
                          Registrado el {fCreated.full}
                        </div>
                        <button
                          type="button"
                          onClick={() => handleFiles(item)}
                          className="text-xs font-medium text-blue-600 hover:text-blue-800 flex items-center gap-1 hover:underline"
                        >
                          <span aria-hidden="true" className="fas fa-paperclip"></span>
                          {files.length > 0
                            ? "Gestionar adjuntos"
                            : "Adjuntar documentos"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Lightbox fullscreen con zoom + pan */}
      {lightboxImages.length > 0 && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center select-none overscroll-contain"
          onClick={() => { setLightboxImages([]); setLbScale(1); setLbPan({ x: 0, y: 0 }); }}
          onWheel={(e) => {
            const delta = e.deltaY > 0 ? -0.2 : 0.2;
            setLbScale((s) => Math.min(8, Math.max(0.5, s + delta)));
          }}
        >
          <div className="absolute top-4 right-4 flex items-center gap-2 z-20">
            <button
              onClick={(e) => { e.stopPropagation(); setLbScale((s) => Math.min(8, s + 0.3)); }}
              className="w-9 h-9 rounded-full bg-white/20 text-white flex items-center justify-center hover:bg-white/40 transition"
              title="Acercar"
            >
              <span aria-hidden="true" className="fas fa-search-plus"></span>
            </button>
            <span className="text-sm text-white/70 min-w-[40px] text-center font-mono">
              {Math.round(lbScale * 100)}%
            </span>
            <button
              onClick={(e) => { e.stopPropagation(); setLbScale((s) => Math.max(0.5, s - 0.3)); }}
              className="w-9 h-9 rounded-full bg-white/20 text-white flex items-center justify-center hover:bg-white/40 transition"
              title="Alejar"
            >
              <span aria-hidden="true" className="fas fa-search-minus"></span>
            </button>
            {lbScale !== 1 && (
              <button
                onClick={(e) => { e.stopPropagation(); setLbScale(1); setLbPan({ x: 0, y: 0 }); }}
                className="w-9 h-9 rounded-full bg-white/20 text-white flex items-center justify-center hover:bg-white/40 transition"
                title="Restablecer zoom"
              >
                <span aria-hidden="true" className="fas fa-undo"></span>
              </button>
            )}
            <button
              onClick={() => { setLightboxImages([]); setLbScale(1); setLbPan({ x: 0, y: 0 }); }}
              className="w-10 h-10 rounded-full bg-white/20 text-white flex items-center justify-center hover:bg-white/40 transition"
            >
              <span aria-hidden="true" className="fas fa-times text-xl"></span>
            </button>
          </div>
          <span className="absolute top-4 left-4 text-white/70 text-sm font-medium z-20">
            {lightboxIndex + 1}/{lightboxImages.length}
          </span>
          {lightboxImages.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxIndex((i) => (i === 0 ? lightboxImages.length - 1 : i - 1));
                  setLbScale(1);
                  setLbPan({ x: 0, y: 0 });
                }}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/20 text-white flex items-center justify-center hover:bg-white/40 transition z-20"
              >
                <span aria-hidden="true" className="fas fa-chevron-left text-2xl"></span>
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxIndex((i) => (i === lightboxImages.length - 1 ? 0 : i + 1));
                  setLbScale(1);
                  setLbPan({ x: 0, y: 0 });
                }}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/20 text-white flex items-center justify-center hover:bg-white/40 transition z-20"
              >
                <span aria-hidden="true" className="fas fa-chevron-right text-2xl"></span>
              </button>
            </>
          )}
          <div
            className="max-h-screen max-w-screen overflow-hidden cursor-grab active:cursor-grabbing"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => {
              setLbDrag({ startX: e.clientX, startY: e.clientY, panX: lbPan.x, panY: lbPan.y });
            }}
            onMouseMove={(e) => {
              if (!lbDrag) return;
              setLbPan({ x: lbDrag.panX + e.clientX - lbDrag.startX, y: lbDrag.panY + e.clientY - lbDrag.startY });
            }}
            onMouseUp={() => setLbDrag(null)}
            onMouseLeave={() => setLbDrag(null)}
          >
            <img
              src={lightboxImages[lightboxIndex]?.url}
              alt=""
              className="max-h-[90vh] max-w-[90vw] object-contain pointer-events-none"
              draggable={false}
              style={{
                transform: `translate(${lbPan.x}px, ${lbPan.y}px) scale(${lbScale})`,
                transformOrigin: 'center center',
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

// --- MODAL DE GESTIÓN DE ARCHIVOS CON METADATA ---
const FollowUpFileManager = ({ item, reload, onClose, field }) => {
  const existingFiles = item.description4 ? JSON.parse(item.description4) : [];
  const [files, setFiles] = useState(existingFiles);
  const [uploading, setUploading] = useState(false);
  const [pasting, setPasting] = useState(false);

  // Estados para el nuevo archivo
  const [selectedFile, setSelectedFile] = useState(null);
  const [newFileMeta, setNewFileMeta] = useState({ name: "", description: "" });
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
      // Pre-llenar nombre si está vacío
      if (!newFileMeta.name) {
        setNewFileMeta((prev) => ({
          ...prev,
          name: e.target.files[0].name.split(".")[0],
        }));
      }
    }
  };

  async function uploadFileDirect(file, name, description) {
    setUploading(true);
    const formData = new FormData();
    formData.append("Name", file.name);
    formData.append("File", file);
    formData.append("originalName", file.name);
    formData.append("Status", "verified");

    try {
      const response = await requestAuth("post", "/files", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const fileUrl = response.data.url || response.data;

      const newFileEntry = {
        url: fileUrl,
        fileName: file.name,
        name: name || file.name.split(".")[0],
        description: description || "",
        uploadDate: new Date().toISOString(),
        type: file.type,
      };

      const updatedFiles = [...files, newFileEntry];

      await requestAuth("put", `/dataSet/procedure/subData/${item.id}`, {
        ...item,
        Description4: JSON.stringify(updatedFiles),
        dataSetId: field.dataSetId,
      });

      setFiles(updatedFiles);
      toast.success("Documento adjuntado con éxito");
      reload();
    } catch (error) {
      console.error(error);
      toast.error("Error al subir el archivo");
    } finally {
      setUploading(false);
    }
  }

  const handlePaste = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith("image/")) {
        e.preventDefault();
        setPasting(true);
        const blob = item.getAsFile();
        if (!blob) continue;
        const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
        const file = new File([blob], `Captura_${timestamp}.png`, { type: blob.type });
        await uploadFileDirect(file, `Captura_${timestamp}`, "Pegado desde portapapeles");
        setPasting(false);
        break;
      }
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return toast.warning("Selecciona un archivo");
    if (!newFileMeta.name) return toast.warning("Ponle un título al documento");
    await uploadFileDirect(selectedFile, newFileMeta.name, newFileMeta.description);
    setSelectedFile(null);
    setNewFileMeta({ name: "", description: "" });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeFile = async (indexToRemove) => {
    if (!window.confirm("¿Borrar este documento?")) return;

    const updatedFiles = files.filter((_, i) => i !== indexToRemove);
    try {
      await requestAuth("put", `/dataSet/procedure/subData/${item.id}`, {
        ...item,
        Description4: JSON.stringify(updatedFiles),
        dataSetId: field.dataSetId,
      });
      setFiles(updatedFiles);
      reload();
      toast.success("Documento eliminado");
    } catch (e) {
      toast.error("Error al eliminar");
    }
  };

  return (
    <div className="bg-white p-6 w-[600px] max-w-full rounded-lg shadow-xl">
      <div className="flex justify-between items-center mb-4 pb-2 border-b">
        <H3>Documentos: {item.label}</H3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <span aria-hidden="true" className="fas fa-times"></span>
        </button>
      </div>

      {/* Formulario de Subida */}
      <div className="bg-blue-50 p-4 rounded-lg mb-6 border border-blue-100">
        <h4 className="text-sm font-bold text-blue-800 mb-3">
          <span aria-hidden="true" className="fas fa-cloud-upload-alt mr-1"></span> Nuevo Documento
        </h4>

        <div className="grid grid-cols-1 gap-3">
          {/* Zona de pegado (Ctrl+V) */}
          <div
            onPaste={handlePaste}
            className={`relative border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
              pasting
                ? "border-green-400 bg-green-50"
                : "border-gray-300 bg-white hover:border-blue-400 hover:bg-blue-50/50"
            }`}
          >
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              className="hidden"
            />
            {pasting ? (
              <div className="flex items-center justify-center gap-2 text-green-700">
                <span aria-hidden="true" className="fas fa-spinner fa-spin"></span>
                <span className="text-sm font-medium">Subiendo captura...</span>
              </div>
            ) : (
              <div className="flex items-center justify-center gap-2 text-gray-500">
                <span aria-hidden="true" className="fas fa-paste text-lg"></span>
                <span className="text-sm">Pegar imagen (Ctrl+V)</span>
                <span className="text-xs text-gray-400">o</span>
                <label className="text-sm text-blue-600 hover:text-blue-800 cursor-pointer underline">
                  seleccionar archivo
                  <input
                    type="file"
                    onChange={(e) => {
                      if (e.target.files && e.target.files[0]) {
                        setSelectedFile(e.target.files[0]);
                        if (!newFileMeta.name) {
                          setNewFileMeta((prev) => ({
                            ...prev,
                            name: e.target.files[0].name.split(".")[0],
                          }));
                        }
                      }
                    }}
                    className="hidden"
                  />
                </label>
              </div>
            )}
          </div>

          {selectedFile && (
            <div className="animate-fade-in-down space-y-2 mt-2">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[10px] uppercase text-gray-500">
                    Título del Doc.
                  </Label>
                  <input
                    className="w-full border rounded p-1.5 text-sm focus:ring-1 ring-blue-400 outline-none"
                    placeholder="Ej: Factura Comercial"
                    value={newFileMeta.name}
                    onChange={(e) =>
                      setNewFileMeta({ ...newFileMeta, name: e.target.value })
                    }
                  />
                </div>
                <div>
                  <Label className="text-[10px] uppercase text-gray-500">
                    Descripción (Opcional)
                  </Label>
                  <input
                    className="w-full border rounded p-1.5 text-sm focus:ring-1 ring-blue-400 outline-none"
                    placeholder="Ej: Versión firmada"
                    value={newFileMeta.description}
                    onChange={(e) =>
                      setNewFileMeta({
                        ...newFileMeta,
                        description: e.target.value,
                      })
                    }
                  />
                </div>
              </div>
              <div className="flex justify-end pt-2">
                <Button
                  onClick={handleUpload}
                  disabled={uploading}
                  className="py-1 px-3 text-xs"
                >
                  {uploading ? (
                    <span aria-hidden="true" className="fas fa-spinner fa-spin"></span>
                  ) : (
                    <>
                      <span aria-hidden="true" className="fas fa-save mr-1"></span> Guardar Archivo
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Lista de Archivos Existentes */}
      <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
        {files.length === 0 && (
          <p className="text-center text-gray-400 text-sm italic">
            No hay documentos adjuntos.
          </p>
        )}

        {files.map((f, i) => (
          <div
            key={i}
            className="flex justify-between items-center p-3 bg-white border border-gray-200 rounded-lg hover:shadow-sm transition"
          >
            <div className="flex items-center gap-3">
              <div className="bg-gray-100 p-2 rounded text-gray-600">
                <span aria-hidden="true" className="fas fa-file-alt"></span>
              </div>
              <div>
                <p className="text-sm font-bold text-gray-800">{f.name}</p>
                <div className="text-xs text-gray-500 flex gap-2">
                  <span>{f.description}</span>
                  <span className="text-gray-300">|</span>
                  <span>{formatDate(f.uploadDate).full}</span>
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <a
                href={f.url}
                target="_blank"
                rel="noreferrer"
                className="text-blue-500 hover:text-blue-700 p-1"
                title="Ver"
              >
                  <span aria-hidden="true" className="fas fa-eye"></span>
              </a>
              <button
                onClick={() => removeFile(i)}
                className="text-red-400 hover:text-red-600 p-1"
                title="Borrar"
              >
                <span aria-hidden="true" className="fas fa-trash"></span>
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// --- FORMULARIO PRINCIPAL (Igual que antes pero con estilo pulido) ---
const FormAddFollowUp = ({ existingItem, field, reload, onClose }) => {
  const meta = safeJsonParse(existingItem?.description2, {});

  const [formData, setFormData] = useState({
    title: existingItem?.label || "",
    eventDate: meta.eventDate || new Date().toISOString().split("T")[0],
    category: meta.category || "OTRO",
    location: meta.location || "",
    comment: meta.comment || "",
  });

  const handleSubmit = async () => {
    if (!formData.title) return toast.error("El título es obligatorio");

    const payload = {
      Label: formData.title,
      Description2: JSON.stringify({
        ...meta,
        comment: formData.comment,
        category: formData.category,
        location: formData.location,
        eventDate: formData.eventDate,
        comments: getEventComments(meta),
      }),
      Description4: existingItem?.description4 || "[]",
      dataSetId: field.dataSetId,
    };

    try {
      await requestAuth(
        existingItem ? "put" : "post",
        existingItem
          ? `/dataSet/procedure/subData/${existingItem.id}`
          : `/dataSet/procedure/${field.id}/field/${field.fieldId}/subdata`,
        payload,
      );
      toast.success("Guardado exitosamente");
      reload();
      onClose();
    } catch (error) {
      toast.error("Error al guardar");
    }
  };

  return (
    <div className="bg-white p-6 rounded-xl shadow-2xl w-[500px] max-w-full">
      <H3 className="mb-4 text-gray-800 border-b pb-2">
        {existingItem ? "Editar Evento" : "Nuevo Hito en Bitácora"}
      </H3>

      <div className="space-y-4">
        <div>
          <Label className="text-xs font-bold text-gray-500 uppercase mb-1">
            Título del Evento
          </Label>
          <input
            value={formData.title}
            onChange={(e) =>
              setFormData({ ...formData, title: e.target.value })
            }
            className="w-full border-gray-300 border rounded-lg p-2.5 focus:ring-2 ring-blue-100 outline-none text-gray-700 font-medium"
            placeholder="Ej: Llegada a Zona Franca"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-xs font-bold text-gray-500 uppercase mb-1">
              Fecha del Evento
            </Label>
            <input
              type="date"
              value={formData.eventDate}
              onChange={(e) =>
                setFormData({ ...formData, eventDate: e.target.value })
              }
              className="w-full border-gray-300 border rounded-lg p-2 focus:ring-2 ring-blue-100 outline-none text-sm"
            />
          </div>
          <div>
            <Label className="text-xs font-bold text-gray-500 uppercase mb-1">
              Categoría
            </Label>
            <select
              value={formData.category}
              onChange={(e) =>
                setFormData({ ...formData, category: e.target.value })
              }
              className="w-full border-gray-300 border rounded-lg p-2 focus:ring-2 ring-blue-100 outline-none text-sm bg-white"
            >
              {Object.entries(CATEGORIES).map(([key, val]) => (
                <option key={key} value={key}>
                  {val.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <Label className="text-xs font-bold text-gray-500 uppercase mb-1">
            Ubicación
          </Label>
          <div className="relative">
            <span aria-hidden="true" className="fas fa-map-marker-alt absolute left-3 top-3 text-gray-400"></span>
            <input
              value={formData.location}
              onChange={(e) =>
                setFormData({ ...formData, location: e.target.value })
              }
              className="w-full border-gray-300 border rounded-lg p-2 pl-9 focus:ring-2 ring-blue-100 outline-none text-sm"
              placeholder="Ubicación física (opcional)"
            />
          </div>
        </div>

        <div>
          <Label className="text-xs font-bold text-gray-500 uppercase mb-1">
            Observaciones
          </Label>
          <textarea
            rows="3"
            value={formData.comment}
            onChange={(e) =>
              setFormData({ ...formData, comment: e.target.value })
            }
            className="w-full border-gray-300 border rounded-lg p-2 focus:ring-2 ring-blue-100 outline-none text-sm resize-none"
            placeholder="Detalles relevantes..."
          />
        </div>

        <div className="pt-4 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-500 hover:bg-gray-100 rounded-lg transition"
          >
            Cancelar
          </button>
          <Button
            onClick={handleSubmit}
            className="px-6 py-2 rounded-lg shadow-md"
          >
            Guardar
          </Button>
        </div>
      </div>
    </div>
  );
};

export default InputSubDataFollowUpAdd;
